// Firebase Firestore Collections Structure for HoppX CRM System

export const COLLECTIONS = {
  // Core CRM Collections
  CONTACTS: 'crm_contacts',
  LEADS: 'crm_leads', 
  OPPORTUNITIES: 'crm_opportunities',
  PIPELINES: 'crm_pipelines',
  CAMPAIGNS: 'crm_campaigns',
  ACTIVITIES: 'crm_activities',
  NOTES: 'crm_notes',
  TASKS: 'crm_tasks',
  
  // Existing Collections
  USERS: 'users',
  AFFILIATES: 'affiliates',
  OFFERS: 'offers'
};

// Contact Structure
export const CONTACT_TEMPLATE = {
  id: '',
  name: '',
  email: '',
  phone: '',
  company: '',
  position: '',
  source: '', // 'website', 'manual', 'import', 'affiliate'
  status: 'active', // 'active', 'inactive', 'blocked'
  tags: [],
  customFields: {},
  assignedTo: '',
  createdAt: null,
  updatedAt: null,
  lastContactDate: null,
  leadScore: 0,
  notes: []
};

// Lead Structure
export const LEAD_TEMPLATE = {
  id: '',
  contactId: '',
  source: '', // 'website_form', 'phone_call', 'email', 'social_media'
  status: 'new', // 'new', 'contacted', 'qualified', 'unqualified', 'converted'
  priority: 'medium', // 'low', 'medium', 'high'
  assignedTo: '',
  tags: [],
  customFields: {},
  createdAt: null,
  updatedAt: null,
  convertedAt: null,
  notes: []
};

// Opportunity Structure
export const OPPORTUNITY_TEMPLATE = {
  id: '',
  name: '',
  contactId: '',
  leadId: '',
  pipelineId: '',
  stageId: '',
  value: 0,
  probability: 0,
  expectedCloseDate: null,
  actualCloseDate: null,
  status: 'open', // 'open', 'won', 'lost'
  priority: 'medium',
  assignedTo: '',
  tags: [],
  customFields: {},
  createdAt: null,
  updatedAt: null,
  notes: [],
  activities: []
};

// Pipeline Structure
export const PIPELINE_TEMPLATE = {
  id: '',
  name: '',
  description: '',
  type: 'sales', // 'sales', 'support', 'custom'
  isActive: true,
  stages: [
    {
      id: '',
      name: '',
      color: '',
      order: 0,
      probability: 0
    }
  ],
  createdBy: '',
  createdAt: null,
  updatedAt: null
};

// Campaign Structure
export const CAMPAIGN_TEMPLATE = {
  id: '',
  name: '',
  type: 'email', // 'email', 'sms', 'push', 'social'
  status: 'draft', // 'draft', 'active', 'paused', 'completed'
  subject: '',
  content: '',
  targetAudience: {
    type: 'all', // 'all', 'tags', 'custom'
    criteria: {}
  },
  schedule: {
    type: 'immediate', // 'immediate', 'scheduled', 'recurring'
    scheduledAt: null,
    recurring: null
  },
  stats: {
    sent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    bounced: 0,
    unsubscribed: 0
  },
  createdBy: '',
  createdAt: null,
  updatedAt: null
};

// Activity Structure
export const ACTIVITY_TEMPLATE = {
  id: '',
  type: 'note', // 'note', 'call', 'email', 'meeting', 'task'
  subject: '',
  description: '',
  contactId: '',
  opportunityId: '',
  userId: '',
  dueDate: null,
  completedAt: null,
  status: 'pending', // 'pending', 'completed', 'cancelled'
  priority: 'medium',
  createdAt: null,
  updatedAt: null
};

// Default Pipelines
export const DEFAULT_PIPELINES = [
  {
    id: 'sales-pipeline',
    name: 'Értékesítési Pipeline',
    description: 'Általános értékesítési folyamat',
    type: 'sales',
    isActive: true,
    stages: [
      { id: 'lead', name: 'Lead', color: 'bg-yellow-500', order: 1, probability: 10 },
      { id: 'qualified', name: 'Kvalifikált', color: 'bg-blue-500', order: 2, probability: 25 },
      { id: 'proposal', name: 'Ajánlat', color: 'bg-purple-500', order: 3, probability: 50 },
      { id: 'negotiation', name: 'Tárgyalás', color: 'bg-orange-500', order: 4, probability: 75 },
      { id: 'closed-won', name: 'Megnyert', color: 'bg-green-500', order: 5, probability: 100 },
      { id: 'closed-lost', name: 'Elveszett', color: 'bg-red-500', order: 6, probability: 0 }
    ]
  },
  {
    id: 'mobilhaz-pipeline',
    name: 'Mobilház Értékesítés',
    description: 'Mobilház specifikus értékesítési folyamat',
    type: 'sales',
    isActive: true,
    stages: [
      { id: 'inquiry', name: 'Érdeklődés', color: 'bg-blue-400', order: 1, probability: 15 },
      { id: 'site-visit', name: 'Helyszíni Felmérés', color: 'bg-indigo-500', order: 2, probability: 30 },
      { id: 'quote', name: 'Árajánlat', color: 'bg-purple-500', order: 3, probability: 50 },
      { id: 'contract', name: 'Szerződés', color: 'bg-orange-500', order: 4, probability: 80 },
      { id: 'production', name: 'Gyártás', color: 'bg-yellow-500', order: 5, probability: 90 },
      { id: 'delivered', name: 'Átadva', color: 'bg-green-500', order: 6, probability: 100 },
      { id: 'cancelled', name: 'Lemondva', color: 'bg-red-500', order: 7, probability: 0 }
    ]
  }
];

// Firebase Helper Functions
export const createContact = async (db, contactData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.CONTACTS), {
    ...CONTACT_TEMPLATE,
    ...contactData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const createLead = async (db, leadData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.LEADS), {
    ...LEAD_TEMPLATE,
    ...leadData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const createOpportunity = async (db, opportunityData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.OPPORTUNITIES), {
    ...OPPORTUNITY_TEMPLATE,
    ...opportunityData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const createPipeline = async (db, pipelineData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.PIPELINES), {
    ...PIPELINE_TEMPLATE,
    ...pipelineData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

export const initializeDefaultPipelines = async (db, userId) => {
  const pipelinesRef = collection(db, COLLECTIONS.PIPELINES);
  
  for (const pipeline of DEFAULT_PIPELINES) {
    await addDoc(pipelinesRef, {
      ...pipeline,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
};
